import React, { useMemo } from 'react';
import { Building2, Users, Activity } from 'lucide-react';
import { PlatformStats } from '../../../services/api';

interface DashboardViewProps {
  stats: PlatformStats;
}

export const DashboardView = React.memo(function DashboardView({ stats }: DashboardViewProps): React.JSX.Element {
  if (!stats) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-zinc-400">Loading statistics...</p>
      </div>
    );
  }

  const planStatsEntries = useMemo(() => 
    stats.planStats ? Object.entries(stats.planStats) : [],
    [stats.planStats]
  );

  const topRestaurants = useMemo(() => 
    Array.isArray(stats.topRestaurants) ? stats.topRestaurants : [],
    [stats.topRestaurants]
  );

  const recentActivity = useMemo(() => 
    Array.isArray(stats.recentActivity) ? stats.recentActivity : [],
    [stats.recentActivity]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">Dashboard</h2>
        <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">Platform overview and statistics</p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="group bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl hover:shadow-2xl hover:shadow-amber-600/10 dark:hover:shadow-amber-600/10 transition-all duration-300 hover:border-amber-600/40 dark:hover:border-amber-600/40 hover:border-amber-500/40">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm font-medium uppercase tracking-wide">Total Restaurants</h3>
            <div className="w-12 h-12 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-xl flex items-center justify-center group-hover:from-amber-600/30 group-hover:to-amber-500/30 dark:group-hover:from-amber-600/30 dark:group-hover:to-amber-500/30 transition-all duration-300 group-hover:scale-110">
              <Building2 className="w-6 h-6 text-amber-500" />
            </div>
          </div>
          <p className="text-4xl font-bold text-amber-50 dark:text-amber-50 text-zinc-900 mb-2 transition-all duration-300 group-hover:scale-105">{stats.totalRestaurants}</p>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700 rounded-lg font-medium">{stats.activeRestaurants} active</span>
            <span className="px-2 py-1 bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 text-zinc-400 dark:text-zinc-400 text-zinc-600 rounded-lg">{stats.inactiveRestaurants} inactive</span>
          </div>
        </div>

        <div className="group bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-blue-600/20 dark:border-blue-600/20 border-blue-300/20 p-6 shadow-xl hover:shadow-2xl hover:shadow-blue-600/10 dark:hover:shadow-blue-600/10 transition-all duration-300 hover:border-blue-600/40 dark:hover:border-blue-600/40 hover:border-blue-500/40">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm font-medium uppercase tracking-wide">Total Users</h3>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600/20 to-blue-500/20 dark:from-blue-600/20 dark:to-blue-500/20 from-blue-100 to-blue-50 rounded-xl flex items-center justify-center group-hover:from-blue-600/30 group-hover:to-blue-500/30 dark:group-hover:from-blue-600/30 dark:group-hover:to-blue-500/30 transition-all duration-300 group-hover:scale-110">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <p className="text-4xl font-bold text-amber-50 dark:text-amber-50 text-zinc-900 mb-2 transition-all duration-300 group-hover:scale-105">{stats.totalUsers}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600">Platform users across all restaurants</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
          <h3 className="text-zinc-300 dark:text-zinc-300 text-zinc-700 text-sm font-semibold mb-4 uppercase tracking-wide">Plan Distribution</h3>
          <div className="space-y-3">
            {planStatsEntries.length > 0 ? planStatsEntries.map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all">
                <span className="text-zinc-300 dark:text-zinc-300 text-zinc-700 font-medium">{plan}</span>
                <span className="px-3 py-1 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 text-amber-400 dark:text-amber-400 text-amber-700 rounded-lg font-semibold">{count as number}</span>
              </div>
            )) : (
              <p className="text-zinc-500 text-sm">No plan data available</p>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
          <h3 className="text-zinc-300 dark:text-zinc-300 text-zinc-700 text-sm font-semibold mb-4 uppercase tracking-wide">Branches</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl">
              <span className="text-zinc-300 dark:text-zinc-300 text-zinc-700 font-medium">Total Branches</span>
              <span className="px-3 py-1 bg-zinc-700/50 dark:bg-zinc-700/50 bg-zinc-200 text-amber-50 dark:text-amber-50 text-zinc-900 rounded-lg font-semibold">{stats.totalBranches}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl">
              <span className="text-zinc-300 dark:text-zinc-300 text-zinc-700 font-medium">Active Branches</span>
              <span className="px-3 py-1 bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700 rounded-lg font-semibold">{stats.activeBranches}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Restaurants */}
      {topRestaurants.length > 0 && (
        <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
          <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Top Restaurants
          </h3>
          <div className="space-y-3">
            {topRestaurants.map((restaurant, index) => (
              <div key={restaurant.restaurantId || index} className="flex items-center justify-between p-4 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all border border-zinc-800/50 dark:border-zinc-800/50 border-zinc-300/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-600/30 to-amber-500/30 dark:from-amber-600/30 dark:to-amber-500/30 from-amber-100 to-amber-50 rounded-lg flex items-center justify-center">
                    <span className="text-amber-400 dark:text-amber-400 text-amber-700 font-bold text-lg">#{index + 1}</span>
                  </div>
                  <div>
                    <span className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold block">{restaurant.restaurantName || 'Unknown'}</span>
                    <span className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-xs">{restaurant.restaurantPlan || 'N/A'} Plan</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-6 shadow-xl">
          <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-6 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Activity
          </h3>
          <div className="space-y-2">
            {recentActivity.map((activity) => (
              <div key={activity._id || Math.random()} className="flex items-center gap-3 p-3 bg-zinc-900/50 dark:bg-zinc-900/50 bg-zinc-100 rounded-xl hover:bg-zinc-900/70 dark:hover:bg-zinc-900/70 hover:bg-zinc-200 transition-all border border-zinc-800/50 dark:border-zinc-800/50 border-zinc-300/50">
                <div className="w-8 h-8 bg-amber-600/20 dark:bg-amber-600/20 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1">
                  <span className="text-zinc-300 dark:text-zinc-300 text-zinc-700 font-medium">{(activity.action || '').replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-500 text-zinc-600 mt-1">
                    {activity.userId?.name && (
                      <span>by {activity.userId.name}</span>
                    )}
                    {activity.restaurantId?.name && (
                      <span>• {activity.restaurantId.name}</span>
                    )}
                  </div>
                </div>
                <span className="text-zinc-500 dark:text-zinc-500 text-zinc-600 text-xs">
                  {activity.createdAt ? new Date(activity.createdAt).toLocaleString() : 'Unknown date'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

